// Pure derivation for the composite zone capability badge (story #497), extracted so it can be
// tested with `bun test` without a DOM (the frontend has no RTL). Maps a `ZoneCapability` —
// verbatim from the agent zone endpoints, no client-side re-derivation — to the tone, label, and
// title each of its three parts (zone signal, serving-equipment behaviour, combined verdict)
// renders. `no_equipment` gets its own `empty` tone: the honest "no equipment link for this
// domain", never a false active.
import type { ZoneCapability } from "@/lib/api/agent";
import type { CardTone } from "@/lib/card-styles";

export interface CapabilityPartView {
  tone: CardTone;
  label: string;
  title: string;
}

export interface ZoneCapabilityBadgeView {
  zoneSignal: CapabilityPartView;
  equipmentBehavior: CapabilityPartView;
  combined: CapabilityPartView;
}

// Labels mirror the honest status vocabulary introduced for equipment capabilities (story #926
// task #937, docs/spec/concepts.md §2.4): "aktiivinen" / "oppii" / "ei dataa" read the same
// whether the badge is a plain KB behaviour or this zone composite.
const ZONE_SIGNAL_LABEL: Record<ZoneCapability["zone_signal"], string> = {
  active: "aktiivinen",
  no_data: "ei dataa",
  unready: "oppii",
};

const ZONE_SIGNAL_TONE: Record<ZoneCapability["zone_signal"], CardTone> = {
  active: "ok",
  no_data: "warn",
  unready: "learning",
};

const EQUIPMENT_BEHAVIOR_LABEL: Record<
  ZoneCapability["equipment_behavior"],
  string
> = {
  active: "aktiivinen",
  fail: "havainto",
  no_data: "ei dataa",
  no_equipment: "ei laiteyhteyttä",
};

const EQUIPMENT_BEHAVIOR_TONE: Record<
  ZoneCapability["equipment_behavior"],
  CardTone
> = {
  active: "ok",
  fail: "warn",
  no_data: "warn",
  no_equipment: "empty",
};

const COMBINED_LABEL: Record<ZoneCapability["combined"], string> = {
  active: "aktiivinen",
  fail: "havainto",
  no_data: "ei dataa",
  no_equipment: "ei laiteyhteyttä",
  unready: "oppii",
};

const COMBINED_TONE: Record<ZoneCapability["combined"], CardTone> = {
  active: "ok",
  fail: "warn",
  no_data: "warn",
  no_equipment: "empty",
  unready: "learning",
};

export function zoneCapabilityBadgeView(
  capability: ZoneCapability,
): ZoneCapabilityBadgeView {
  return {
    zoneSignal: {
      tone: ZONE_SIGNAL_TONE[capability.zone_signal],
      label: ZONE_SIGNAL_LABEL[capability.zone_signal],
      title: `Vyöhykkeen oma signaali: ${ZONE_SIGNAL_LABEL[capability.zone_signal]}`,
    },
    equipmentBehavior: {
      tone: EQUIPMENT_BEHAVIOR_TONE[capability.equipment_behavior],
      label: EQUIPMENT_BEHAVIOR_LABEL[capability.equipment_behavior],
      title:
        capability.equipment_behavior === "no_equipment"
          ? "Ei tälle alueelle liitettyä laitetta, joka toteuttaisi tätä toimintoa"
          : `Palveleva laite: ${EQUIPMENT_BEHAVIOR_LABEL[capability.equipment_behavior]}`,
    },
    combined: {
      tone: COMBINED_TONE[capability.combined],
      label: COMBINED_LABEL[capability.combined],
      title:
        "Yhdistetty tila: vyöhykkeen signaali + palvelevan laitteen toiminto",
    },
  };
}
